import apiClient from "./client";
import type {
  ConfigPackage,
  ConfigPackageActionResult,
  ConfigPackageList,
  ConfigTypeIngestStart,
  ConfigTypeUploadResult,
  ReferenceDataAgentList,
  ReferenceDataEditOperation,
  ReferenceDataEditResult,
  ReferenceDataIngestStart,
  ReferenceDataPromotionResult,
  ReferenceDataRecordList,
  ReferenceDataRecordRollbackResult,
  ReferenceDataRecordVersionList,
  ReferenceDataRunList,
  ReferenceDataRunStatus,
  ReferenceDataSchema,
  ReferenceDataSourceList,
  ReferenceDataUploadResult,
  ReferenceDataVersionList,
} from "@/types/reference-data";

const BASE = "/api/admin/reference-data";

/** Timeout do upload por tabela; a planilha SAP grande entra pelo tipo de configuração. */
const UPLOAD_TIMEOUT_MS = 120_000;

/** `dataset` que o backend grava no relatório da run de uma release da planilha SSCUI. */
export const SSCUI_LIST_DATASET = "sap_sscui_list";

/** List every reference-data source with its live active version + audit. */
export async function listReferenceDataSources(): Promise<ReferenceDataSourceList> {
  const res = await apiClient.get<ReferenceDataSourceList>(`${BASE}/sources`);
  return res.data;
}

/** Paginated active rows (current_version = TRUE) of one source. */
export async function listReferenceDataRecords(
  tableName: string,
  params: { limit?: number; offset?: number; q?: string; column?: string } = {},
): Promise<ReferenceDataRecordList> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.q) qs.set("q", params.q);
  if (params.column) qs.set("column", params.column);
  const q = qs.toString();
  const res = await apiClient.get<ReferenceDataRecordList>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/records${q ? `?${q}` : ""}`,
  );
  return res.data;
}

/** Columns a client-uploaded file may contain (schema minus bookkeeping). */
export async function getReferenceDataSchema(
  tableName: string,
): Promise<ReferenceDataSchema> {
  const res = await apiClient.get<ReferenceDataSchema>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/schema`,
  );
  return res.data;
}

/** Audit history for one source (upload/ingest/promote/rollback runs). */
export async function getReferenceDataHistory(
  tableName: string,
  params: {
    operation_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<ReferenceDataRunList> {
  const qs = new URLSearchParams();
  if (params.operation_type) qs.set("operation_type", params.operation_type);
  if (params.status) qs.set("status", params.status);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const res = await apiClient.get<ReferenceDataRunList>(
    `${BASE}/audit/${encodeURIComponent(tableName)}${q ? `?${q}` : ""}`,
  );
  return res.data;
}

/** The selectable versions of one source (for the rollback / promote picker). */
export async function listReferenceDataVersions(
  tableName: string,
): Promise<ReferenceDataVersionList> {
  const res = await apiClient.get<ReferenceDataVersionList>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/versions`,
  );
  return res.data;
}

/** Upload a CSV/xlsx to a source (multipart → MinIO, SHA-256 verified). */
export async function uploadReferenceDataFile(
  tableName: string,
  file: File,
): Promise<ReferenceDataUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<ReferenceDataUploadResult>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/upload`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: UPLOAD_TIMEOUT_MS,
    },
  );
  return res.data;
}

/** Fire an async ingestion over a previously uploaded object (202 + run_id). */
export async function ingestReferenceData(
  tableName: string,
  payload: { object_key: string; filename: string; expected_sha256?: string },
): Promise<ReferenceDataIngestStart> {
  const res = await apiClient.post<ReferenceDataIngestStart>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/ingest`,
    payload,
  );
  return res.data;
}

/** Poll one ingestion run for progress / outcome. */
export async function getReferenceDataIngestStatus(
  tableName: string,
  runId: string,
): Promise<ReferenceDataRunStatus> {
  const res = await apiClient.get<ReferenceDataRunStatus>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/ingest/${encodeURIComponent(runId)}`,
  );
  return res.data;
}

/** Promote a staged ingest run to active (per composite key). */
export async function promoteReferenceData(
  tableName: string,
  ingestRunId: string,
): Promise<ReferenceDataPromotionResult> {
  const res = await apiClient.post<ReferenceDataPromotionResult>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/promote`,
    { ingest_run_id: ingestRunId },
  );
  return res.data;
}

/** Roll back to a previous ingest run (re-point active, per key). */
export async function rollbackReferenceData(
  tableName: string,
  ingestRunId: string,
): Promise<ReferenceDataPromotionResult> {
  const res = await apiClient.post<ReferenceDataPromotionResult>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/rollback`,
    { ingest_run_id: ingestRunId },
  );
  return res.data;
}

/** The earlier versions of ONE row (its natural key), for the per-row rollback
 *  dialog. The API lists only versions whose run reached active. */
export async function listRecordVersions(
  tableName: string,
  recordId: string,
): Promise<ReferenceDataRecordVersionList> {
  const res = await apiClient.get<ReferenceDataRecordVersionList>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/records/` +
      `${encodeURIComponent(recordId)}/versions`,
  );
  return res.data;
}

/** Revert ONE row to another version of the same natural key. The other keys are
 *  untouched. 400 if the target belongs to a different key, 409 if it is already
 *  the active one. */
export async function rollbackReferenceDataRecord(
  tableName: string,
  recordId: string,
  targetRowId: string,
): Promise<ReferenceDataRecordRollbackResult> {
  const res = await apiClient.post<ReferenceDataRecordRollbackResult>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/records/` +
      `${encodeURIComponent(recordId)}/rollback`,
    { target_row_id: targetRowId },
  );
  return res.data;
}

/** Stage a batch of row-level create/update edits as a new 'edit' version.
 *  Synchronous: returns the staged run + sanity report (promote it via
 *  promoteReferenceData). A rejected batch comes back as a 422. */
export async function stageReferenceDataEdits(
  tableName: string,
  operations: ReferenceDataEditOperation[],
): Promise<ReferenceDataEditResult> {
  const res = await apiClient.post<ReferenceDataEditResult>(
    `${BASE}/sources/${encodeURIComponent(tableName)}/records/edit`,
    { operations },
  );
  return res.data;
}

/** A planilha da SAP tem uns 108 MB: o upload do tipo ganha 10 minutos. */
const CONFIG_TYPE_UPLOAD_TIMEOUT_MS = 600_000;
// O ingest responde 202 depois de validar o objeto no storage: mais folga que o padrão do cliente.
const CONFIG_TYPE_INGEST_TIMEOUT_MS = 90_000;

const configTypeBase = (key: string) => `${BASE}/config-types/${encodeURIComponent(key)}`;

/** Agentes com seus tipos de configuração, tabelas e release ativa. */
export async function listReferenceDataAgents(): Promise<ReferenceDataAgentList> {
  const res = await apiClient.get<ReferenceDataAgentList>(`${BASE}/agents`);
  return res.data;
}

/** Sobe o arquivo de um tipo de configuração (ex.: a planilha SSCUI da SAP). */
export async function uploadConfigTypeFile(
  key: string,
  file: File,
): Promise<ConfigTypeUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<ConfigTypeUploadResult>(
    `${configTypeBase(key)}/upload`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: CONFIG_TYPE_UPLOAD_TIMEOUT_MS,
    },
  );
  return res.data;
}

/** Dispara a carga do pacote (202 + package_id). */
export async function ingestConfigType(
  key: string,
  payload: {
    object_key: string;
    filename?: string;
    expected_sha256?: string;
    options?: Record<string, string>;
  },
): Promise<ConfigTypeIngestStart> {
  // Só os campos da API: o tipo não precisa do nome do arquivo.
  const body = {
    object_key: payload.object_key,
    expected_sha256: payload.expected_sha256,
    options: payload.options,
  };
  const res = await apiClient.post<ConfigTypeIngestStart>(`${configTypeBase(key)}/ingest`, body, {
    timeout: CONFIG_TYPE_INGEST_TIMEOUT_MS,
  });
  return res.data;
}

/** CSV de exemplo do tipo, para o consultor seguir as colunas. */
export async function downloadConfigTypeTemplate(key: string): Promise<Blob> {
  const res = await apiClient.get<Blob>(`${configTypeBase(key)}/template`, { responseType: "blob" });
  return res.data;
}

/** Estado do pacote e das runs de cada tabela (polling). */
export async function getConfigPackage(key: string, packageId: string): Promise<ConfigPackage> {
  const res = await apiClient.get<ConfigPackage>(
    `${configTypeBase(key)}/ingest/${encodeURIComponent(packageId)}`,
  );
  return res.data;
}

/** Pacotes do tipo, do mais novo ao mais antigo. */
export async function listConfigPackages(key: string): Promise<ConfigPackageList> {
  const res = await apiClient.get<ConfigPackageList>(`${configTypeBase(key)}/releases`);
  return res.data;
}

/** Promove o pacote em todas as tabelas do tipo. */
export async function promoteConfigPackage(
  key: string,
  packageId: string,
): Promise<ConfigPackageActionResult> {
  const res = await apiClient.post<ConfigPackageActionResult>(`${configTypeBase(key)}/promote`, {
    package_id: packageId,
  });
  return res.data;
}

/** Volta todas as tabelas do tipo para um pacote já promovido. */
export async function rollbackConfigPackage(
  key: string,
  packageId: string,
): Promise<ConfigPackageActionResult> {
  const res = await apiClient.post<ConfigPackageActionResult>(`${configTypeBase(key)}/rollback`, {
    package_id: packageId,
  });
  return res.data;
}
